import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import {
    AuthResponse,
    AuthenticationInput,
    ForgetPasswordInput,
    RegisterUserInput,
    RegisterUserResponse,
    ResetPasswordInput,
    TwoFactorCredentialsResponse,
    VerifySessionResponse,
    GenericSuccessResponse,
    PasswordRequirementsResponse,
    RemoveSessionResponse,
    SecurityErrorResponse,
    VerifyGuestAccessResponse,
    GuestUserAuthResponse,
    AuthenticateGuestInput
} from "app/shared/model";

import { mergeMap as _observableMergeMap, catchError as _observableCatch, catchError, switchMap } from 'rxjs/operators';
import { Observable, throwError, of as _observableOf, of } from 'rxjs';
import { API_BASE_URL } from "./service-shared";
import { environment } from "environments/environment";
import { blobToText, throwException } from "./service-functions";

@Injectable({ providedIn: 'root' })
export class SecurityServiceProxy {
    private http: HttpClient;
    private baseUrl: string;
    private basePath: string;

    // private soapClient: SecurityServiceClient;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(
        @Inject(HttpClient) http: HttpClient,
        @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.basePath = environment.config?.securityEndpoint !== undefined ? environment.config.securityEndpoint : '';
        this.baseUrl = (baseUrl !== undefined && baseUrl !== null ? baseUrl : "") + this.basePath;
    }

    /**
     * Authenticates the user specified in the input.
     * @param body (optional) The authentication request input.
     * @return Success
     */
    authenticate(body: AuthenticationInput | undefined): Observable<AuthResponse> {
        let url_ = this.baseUrl + "/Authenticate";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processAuthenticate(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processAuthenticate(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<AuthResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<AuthResponse>;
        }));
    }

    protected processAuthenticate(response: HttpResponseBase): Observable<AuthResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = AuthResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 402) {
            // two fa
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result402: any = null;
                result402 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result402 = AuthResponse.fromJS(result402);
                return _observableOf(result402);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SecurityErrorResponse>(null as any);
    }

    /**
     * Verifies the current session, or creates a new one.
     * @return Success
     */
    verifySession(): Observable<VerifySessionResponse> {
        let url_ = this.baseUrl + '/VerifySession';
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
                "ngsw-bypass": "true"
            })
        };

        return this.http.request("post", url_, options_)
            .pipe(
                switchMap((response_: any) => {
                    return this.processVerifySession(response_);
                }),
                catchError((err: Error) => {
                    return throwError(() => { return err; });
                }));
    }

    protected processVerifySession(response: HttpResponseBase): Observable<VerifySessionResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = VerifySessionResponse.fromJS(resultData200);
                return of(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return of<VerifySessionResponse>(null as any);
    }

    /**
     * Verifies the current session, or creates a new one.
     * @return Success
     */
    removeSession(): Observable<RemoveSessionResponse> {
        let url_ = this.baseUrl + '/RemoveSession';
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({ redirectPath: '/sign-in' });

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processRemoveSession(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processRemoveSession(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<RemoveSessionResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<RemoveSessionResponse>;
        }));
    }

    protected processRemoveSession(response: HttpResponseBase): Observable<RemoveSessionResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = RemoveSessionResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<RemoveSessionResponse>(null as any);
    }

    /**
     * Terminates any existing session, and deletes all cookies for a fresh start next time.
     * @return Success
     */
    logout(): Observable<GenericSuccessResponse> {
        let url_ = this.baseUrl + '/Logout';
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processLogout(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processLogout(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<GenericSuccessResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<GenericSuccessResponse>;
        }));
    }

    protected processLogout(response: HttpResponseBase): Observable<GenericSuccessResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GenericSuccessResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GenericSuccessResponse>(null as any);
    }

    /**
     * Registers a new unverified user credential.
     * @param body (optional) The Register New User input.
     * @return Success
     */
    registerUser(body: RegisterUserInput | undefined): Observable<RegisterUserResponse> {
        let url_ = this.baseUrl + "/SaveUser";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processRegisterUser(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processRegisterUser(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<RegisterUserResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<RegisterUserResponse>;
        }));
    }

    protected processRegisterUser(response: HttpResponseBase): Observable<RegisterUserResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = RegisterUserResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GenericSuccessResponse>(null as any);
    }

    /**
     * Enables two factor authentication if the code is valid for the current user.
     * @return Success
     */
    enableTwoFactor(code: string): Observable<AuthResponse> {
        let url_ = this.baseUrl + "/EnableTwoFactorAuthentication";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({ code });

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processEnableTwoFactor(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processEnableTwoFactor(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<AuthResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<AuthResponse>;
        }));
    }

    protected processEnableTwoFactor(response: HttpResponseBase): Observable<AuthResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = AuthResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 402) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("Two Factor Authentication Code Failure", status, _responseText, _headers);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SecurityErrorResponse>(null as any);
    }

    /**
     * Return TwoFactorCredentialsResponse including qr code
     * @return Success
     */
    getTwoFactorCredentials(): Observable<TwoFactorCredentialsResponse> {
        let url_ = this.baseUrl + "/GetTwoFactorCredentials";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetTwoFactorCredentials(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetTwoFactorCredentials(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<TwoFactorCredentialsResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<TwoFactorCredentialsResponse>;
        }));
    }

    protected processGetTwoFactorCredentials(response: HttpResponseBase): Observable<TwoFactorCredentialsResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = TwoFactorCredentialsResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SecurityErrorResponse>(null as any);
    }

    /**
     * Verifies a two factor authentication code for the current user.
     * @return Success
     */
    verifyTwoFactorCode(code: string): Observable<AuthResponse> {
        let url_ = this.baseUrl + "/VerifyTwoFactorCode";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({ code });

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processVerifyTwoFactorCode(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processVerifyTwoFactorCode(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<AuthResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<AuthResponse>;
        }));
    }

    protected processVerifyTwoFactorCode(response: HttpResponseBase): Observable<AuthResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = AuthResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 402) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("Two Factor Authentication Code Failure", status, _responseText, _headers);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SecurityErrorResponse>(null as any);
    }

    /**
     * Generate email when user forgot password
     * @param body (optional) DTO for forget password
     * @return Success
     */
    forgetPassword(body: ForgetPasswordInput | undefined): Observable<GenericSuccessResponse> {
        let url_ = this.baseUrl + "/SendPasswordChange";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processForgetPassword(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processForgetPassword(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<GenericSuccessResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<GenericSuccessResponse>;
        }));
    }

    protected processForgetPassword(response: HttpResponseBase): Observable<GenericSuccessResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GenericSuccessResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GenericSuccessResponse>(null as any);
    }

    /**
     * reset user's password
     * @param body (optional) DTO for reset password
     * @return Success
     */
    resetPassword(body: ResetPasswordInput | undefined): Observable<GenericSuccessResponse> {
        let url_ = this.baseUrl + "/ChangePassword";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processResetPassword(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processResetPassword(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<GenericSuccessResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<GenericSuccessResponse>;
        }));
    }

    protected processResetPassword(response: HttpResponseBase): Observable<GenericSuccessResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GenericSuccessResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 500) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result500: any = null;
                let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result500 = SecurityErrorResponse.fromJS(resultData500);
                return throwException("An unexpected server error occurred.", status, _responseText, _headers, result500.message || "An unexpected server error occurred");
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SecurityErrorResponse>(null as any);
    }

    /**
     * Validate a given password change token has not expired and is a valid user.
     */
    validatePasswordChangeToken(token: string, username: string): Observable<GenericSuccessResponse> {
        let url_ = this.baseUrl + "/ValidatePasswordChangeToken";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({
            passwordChangeToken: token,
            username
        });

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processValidatePasswordChangeToken(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processValidatePasswordChangeToken(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<GenericSuccessResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<GenericSuccessResponse>;
        }));
    }

    protected processValidatePasswordChangeToken(response: HttpResponseBase): Observable<GenericSuccessResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GenericSuccessResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GenericSuccessResponse>(null as any);
    }

    /**
     * Gets a list of password complexity requirements.
     */
    getPasswordRequirements(): Observable<PasswordRequirementsResponse> {
        let url_ = this.baseUrl + "/GetPasswordRequirements";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({});

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetPasswordRequirements(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetPasswordRequirements(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<PasswordRequirementsResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<PasswordRequirementsResponse>;
        }));
    }

    protected processGetPasswordRequirements(response: HttpResponseBase): Observable<PasswordRequirementsResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = PasswordRequirementsResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<PasswordRequirementsResponse>(null as any);
    }

    verifyGuestAccessGuid(accessGuid: string): Observable<VerifyGuestAccessResponse> {
        let url_ = this.baseUrl + "/VerifyGuestAccessGuid";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({accessGuid});

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processVerifyGuestAccessGuid(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processVerifyGuestAccessGuid(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<VerifyGuestAccessResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<VerifyGuestAccessResponse>;
        }));
    }

    protected processVerifyGuestAccessGuid(response: HttpResponseBase): Observable<VerifyGuestAccessResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = VerifyGuestAccessResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver, true)
        ?? _observableOf<VerifyGuestAccessResponse>(null as any);
    }

    authenticateGuest(input: AuthenticateGuestInput): Observable<GuestUserAuthResponse> {
        let url_ = this.baseUrl + "/AuthenticateGuest";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(input);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_)
            .pipe(_observableMergeMap((response_: any) =>
            {
                return this.processAuthenticateGuest(response_);
            }))
            .pipe(_observableCatch((response_: any) =>
            {
                if (response_ instanceof HttpResponseBase) {
                    try {
                        return this.processAuthenticateGuest(response_ as any);
                    } catch (e) {
                        return throwError(() => new Error(e)) as any as Observable<GuestUserAuthResponse>;
                    }
                } else
                return throwError(() => new Error(response_)) as any as Observable<GuestUserAuthResponse>;
            }));
    }

    protected processAuthenticateGuest(response: HttpResponseBase): Observable<GuestUserAuthResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GuestUserAuthResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 402) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result402: any = null;
                result402 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result402 = GuestUserAuthResponse.fromJS(result402);
                return  _observableOf(result402);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver, true)
        ?? _observableOf<GuestUserAuthResponse>(null as any);
    }

    verifyGuestPasscode(passcode: string): Observable<GuestUserAuthResponse> {
        let url_ = this.baseUrl + "/VerifyGuestPasscode";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify({passcode});

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json;charset=utf-8",
                "Accept": "application/json, text/plain, */*",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processVerifyGuestPasscode(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processVerifyGuestPasscode(response_ as any);
                } catch (e) {
                    return throwError(() => new Error(e)) as any as Observable<GuestUserAuthResponse>;
                }
            } else
                return throwError(() => new Error(response_)) as any as Observable<GuestUserAuthResponse>;
        }));
    }

    protected processVerifyGuestPasscode(response: HttpResponseBase): Observable<GuestUserAuthResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GuestUserAuthResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 402) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result402: any = null;
                result402 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result402 = GuestUserAuthResponse.fromJS(result402);
                return  _observableOf(result402);
            }));
        }

        return this._processErrorResponse(status, responseBlob, _headers, this.jsonParseReviver, true)
        ?? _observableOf<SecurityErrorResponse>(null as any);
    }

    private _processErrorResponse(
        status: number,
        responseBlob: any,
        headers: { [key: string]: any; },
        jsonParseReviver?: ((key: string, value: any) => any) | undefined,
        showResponseObject: boolean = false
    ): Observable<any> {
        if (status === 400) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result400: any = null;
                let resultData400 = _responseText === "" ? null : JSON.parse(_responseText, jsonParseReviver);
                result400 = SecurityErrorResponse.fromJS(resultData400);
                return throwException("Bad Request", status, _responseText, headers, showResponseObject ? result400 : result400.message || "");
            }));
        } else if (status === 401) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result401: any = null;
                let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, jsonParseReviver);
                result401 = SecurityErrorResponse.fromJS(resultData401);
                return throwException("Unauthorized", status, _responseText, headers,  showResponseObject ? result401 : result401.message || "");
            }));
        } else if (status === 403) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result403: any = null;
                let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, jsonParseReviver);
                result403 = SecurityErrorResponse.fromJS(resultData403);
                return throwException("Forbidden", status, _responseText, headers, result403);
            }));
        } else if (status !== 200 && status !== 204) {
            let message: string = '';
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                const resultData = _responseText === "" ? null : JSON.parse(_responseText, jsonParseReviver);
                const result = SecurityErrorResponse.fromJS(resultData) || null;
                message = result?.message ? result.message : "An unexpected server error occurred.";
                return throwException(message, status, _responseText, headers, result);
            }));
        }

        return null;
    }
}
